import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Sparkles, Clock, X, AlertTriangle } from 'lucide-react';

const DISMISS_KEY = 'savenes_trial_banner_dismissed_at';
// We always re-show in the final 24h regardless of dismissal.
const REDISMISS_HOURS = 24;

/**
 * Trial banner — appears at the top of every authenticated page when:
 *   • org.subscription.status === "trialing"
 *   • or the trial has expired and not been converted (urgent state)
 *
 * Copy escalates from "X days left" → "Final day — upgrade today!" →
 * "Trial expired" with a 1-click route to /admin/settings?tab=billing.
 *
 * Designed to be dismissable but persistent within 24h of expiry so users
 * don't accidentally lose access without warning.
 */
export default function TrialBanner() {
  const { sub } = useSubscription();
  const [dismissedRecently, setDismissedRecently] = useState(() => {
    const t = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (!t) return false;
    const hoursSince = (Date.now() - t) / 36e5;
    return hoursSince < REDISMISS_HOURS;
  });
  const navigate = useNavigate();

  if (!sub) return null;
  const status = sub.status;
  const days = sub.days_remaining;
  const hours = sub.hours_remaining;
  const expired = sub.expired;
  const onTrial = status === 'trialing';
  if (!onTrial && !expired) return null;
  // Final 24h overrides dismissal
  const isCritical = expired || (typeof days === 'number' && days <= 1);
  if (!isCritical && dismissedRecently) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissedRecently(true);
  };

  const goUpgrade = () => navigate('/admin/settings?tab=billing');

  // Variants
  let palette = 'from-emerald-50 to-teal-50 border-emerald-200 text-emerald-900';
  let icon = <Sparkles className="w-4 h-4 text-emerald-600 shrink-0" />;
  let title = '';
  let copy = '';
  let ctaLabel = 'Upgrade now';

  if (expired) {
    palette = 'from-rose-50 to-rose-100 border-rose-300 text-rose-900';
    icon = <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />;
    title = 'Your trial has ended';
    copy = 'Pick a plan to keep your forms, patients, and team data accessible.';
    ctaLabel = 'Choose a plan';
  } else if (days <= 0 && hours >= 0) {
    palette = 'from-amber-50 to-rose-100 border-amber-300 text-amber-900';
    icon = <Clock className="w-4 h-4 text-amber-700 shrink-0" />;
    title = `Last day of your trial — ${hours}h left`;
    copy = 'Upgrade now to keep your data and unlock every Savenes feature.';
  } else if (days <= 2) {
    palette = 'from-amber-50 to-yellow-50 border-amber-300 text-amber-900';
    icon = <Clock className="w-4 h-4 text-amber-700 shrink-0" />;
    title = `${days} day${days === 1 ? '' : 's'} left in your trial`;
    copy = 'Lock in pricing today and avoid any interruption to your workflow.';
  } else if (days <= 5) {
    palette = 'from-amber-50 to-amber-100 border-amber-200 text-amber-900';
    icon = <Sparkles className="w-4 h-4 text-amber-600 shrink-0" />;
    title = `Day ${7 - days} of 7 — loving Savenes so far?`;
    copy = 'Upgrade now to lock in your data and get full access to every feature.';
  } else {
    title = `Welcome — ${days} days of free trial remaining`;
    copy = 'Explore the form builder. Upgrade anytime to unlock patients, billing, and analytics.';
  }

  return (
    <div className={`bg-gradient-to-r ${palette} border-b px-4 py-2 flex items-center gap-3`} data-testid="trial-banner">
      {icon}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold leading-tight">{title}</div>
        <div className="text-xs opacity-80 leading-tight truncate">{copy}</div>
      </div>
      <Button
        size="sm"
        onClick={goUpgrade}
        className={`${expired ? 'bg-rose-600 hover:bg-rose-700' : 'bg-[#0E7490] hover:bg-[#0E7490]/90'} text-white text-xs whitespace-nowrap`}
        data-testid="trial-banner-upgrade-btn"
      >
        {ctaLabel}
      </Button>
      {!isCritical && (
        <button onClick={dismiss}
                className="text-current opacity-60 hover:opacity-100 transition-opacity"
                aria-label="Dismiss" data-testid="trial-banner-dismiss-btn">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
